import { useHubble } from '@seek/hubble';
import { useTranslations } from '@vocab/react';
import {
  Badge,
  Box,
  Card,
  Column,
  Columns,
  Divider,
  Heading,
  Inline,
  Secondary,
  Stack,
  Text,
} from 'braid-design-system';
import { Fragment, type FunctionComponent, useEffect, useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { PRODUCT_SET_FAILED_UNAVAILABLE } from 'src/app/shared/constants/productSetStatus';
import { PRICE_PANEL_DISPLAYED } from 'src/app/shared/dataAnalytics/schema/constants';
import { log } from 'src/app/utils/logging';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { PriceCurrencyTax } from 'src/app/v1/views/components/PriceCurrencyTax';
import type { AdProduct, Price } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';

import translations from './.vocab';
import { PricePanelError } from './components/PricePanelError';
import { PricePanelHelpText } from './components/PricePanelHelpText';
import PricePanelLoader from './components/PricePanelLoader';

import * as styles from './index.css';

export type AdProductPriced = Omit<AdProduct, 'price'> & { price: Price };

export const getLowestPricedProduct = (
  adProducts?: Array<AdProductPriced>,
): AdProductPriced | undefined => {
  // adproducts are undefined
  if (!adProducts) {
    return undefined;
  }
  // adproducts is empty array
  if (adProducts.length === 0) {
    return undefined;
  }
  // adproducts only has 1 item
  if (adProducts.length === 1) {
    return adProducts[0];
  }
  // adproducts has 2 or more items
  const lowest = adProducts.reduce((prev, current) =>
    prev.price.netPrice.value < current.price.netPrice.value ? prev : current,
  );

  return lowest;
};

export const PricePanel: FunctionComponent = () => {
  const {
    advertiserNation,
    interactionId,
    jobLocationText,
    jobTitle,
    locationId,
    productSetStatus,
    seekAdvertiserId,
    subCategoryId,
    draftId,
    flowType,
    pageId,
    productSet,
  } = useSelector(
    (state: AppState) => ({
      advertiserNation: state.session.hirerAccount.nation,
      draftId: state.session.draftId,
      flowType: state.tealium.cajaFlowType,
      interactionId: state.session.interactionId,
      jobLocationText: state.session.jobLocationText,
      jobTitle: state.formData.jobTitle,
      locationId: state.formData.jobLocationId,
      pageId: state.session.pageId,
      productSetStatus: state.session.productSetStatus,
      seekAdvertiserId: state.session.seekAdvertiserId,
      subCategoryId: state.session.seekSubClassificationId,
      productSet: state.session.productSet,
    }),
    shallowEqual,
  );

  const { t } = useTranslations(translations);

  const productSetId = productSet?.id;
  const productSetItems = productSet?.items as Array<AdProduct> | undefined;

  const freeAdProducts = productSetItems?.filter(
    (item) =>
      item.__typename === 'AdProduct' &&
      item.price.__typename === 'Price' &&
      item.price.retailPrice.value === 0,
  ) as AdProductPriced[] | undefined;

  const adProducts = productSetItems?.filter(
    (item) =>
      item.__typename === 'AdProduct' && item.price.__typename === 'Price',
  ) as AdProductPriced[] | undefined;

  const lowestPricedProduct = getLowestPricedProduct(adProducts);

  const lowestPricedProductId = lowestPricedProduct?.id;

  const isPriceDiscounted = Boolean(
    (lowestPricedProduct?.price.discount?.discountPercentage ?? 0) > 0,
  );

  const netPrice = lowestPricedProduct?.price.netPrice;
  const retailPrice = lowestPricedProduct?.price.retailPrice;
  const isVariablyPriced = Boolean(lowestPricedProduct?.price.isVariable);

  const tax = lowestPricedProduct?.price.tax ?? undefined;
  const retailTax = lowestPricedProduct?.price.retailTax ?? undefined;

  const netPriceForEvent = useMemo(
    () => ({
      value: netPrice?.value ?? undefined,
      currency: netPrice?.currency ?? undefined,
    }),
    [netPrice?.value, netPrice?.currency],
  );

  const hubble = useHubble();

  if (freeAdProducts && freeAdProducts.length > 1) {
    log.error('PricePanel | multiple free ad products', {
      freeAdProducts,
      advertiserNation,
      interactionId,
      locationId,
      seekAdvertiserId,
    });
  }

  useEffect(() => {
    if (productSetId) {
      const eventData = {
        eventName: PRICE_PANEL_DISPLAYED,
        objectInteraction: 'pay-details',
        adProductSetId: productSetId,
        adProductPricing: [
          {
            position: 1,
            id: lowestPricedProductId,
            price: netPrice?.value,
            currency: netPrice?.currency,
            type: 'AdProduct',
            priceHidden: false,
          },
        ],
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lowestPricedProductId,
    netPrice?.currency,
    netPrice?.value,
    productSetId,
  ]);

  const hasEnteredAllJobDetails =
    Boolean(jobTitle) && Boolean(jobLocationText) && Boolean(subCategoryId);

  const freeAdAvailable = Boolean(freeAdProducts?.length);

  const isLoading = hasEnteredAllJobDetails && !productSet;
  const isDataNotFound =
    hasEnteredAllJobDetails && productSet && !lowestPricedProduct;

  useEffect(() => {
    if (
      draftId &&
      productSetId &&
      lowestPricedProductId &&
      flowType &&
      pageId
    ) {
      hubble.trackEvent('lowest_price_displayed', {
        draftId,
        widgetName: 'LowestPricePreview',
        postingFlowType: flowType,
        postingFlowPageName: pageId,
        productSetId,
        productId: lowestPricedProductId,
        netPrice: netPriceForEvent,
        opportunityId: draftId,
      });
    }
  }, [
    draftId,
    flowType,
    pageId,
    productSetId,
    lowestPricedProductId,
    netPriceForEvent,
    hubble,
  ]);

  return (
    <Card>
      <Stack space="medium">
        <Columns space="none">
          <Column>
            <Heading level="4">{t('price-panel-heading-text')}</Heading>
          </Column>
          <Column width="content">
            {isVariablyPriced && <PricePanelHelpText />}
          </Column>
        </Columns>

        {isLoading && <PricePanelLoader />}
        {isDataNotFound && <PricePanelError />}

        {!hasEnteredAllJobDetails && (
          <Text size="small">{t('price-panel-help-text')}</Text>
        )}

        {hasEnteredAllJobDetails && (
          <Fragment>
            <Text size="small" data={{ testid: 'price-panel-job-title' }}>
              <span className={styles.jobTitle}>{jobTitle}</span>
            </Text>
            <Text size="small">{jobLocationText}</Text>
            {productSetStatus !== PRODUCT_SET_FAILED_UNAVAILABLE && (
              <>
                <Divider weight="regular" />
                <Stack space="small">
                  <Box display="flex" justifyContent="spaceBetween">
                    <Text size="small">{t('price-panel-from-text')} </Text>
                    <Inline space="xsmall" alignY="center" align="right">
                      {isPriceDiscounted && retailPrice && (
                        <s>
                          <PriceCurrencyTax
                            tax={retailTax}
                            size="xsmall"
                            originalPrice={retailPrice.value!}
                            currency={retailPrice.currency}
                            displayTaxSuffix={false}
                          />
                        </s>
                      )}
                      {netPrice && (
                        <PriceCurrencyTax
                          tax={tax}
                          priceWeight="strong"
                          originalPrice={netPrice.value!}
                          currency={netPrice.currency}
                        />
                      )}
                    </Inline>
                  </Box>
                  {isPriceDiscounted && (
                    <Box display="flex" justifyContent="flexEnd">
                      <Badge tone="info">
                        {t('price-panel-discount-text')}
                      </Badge>
                    </Box>
                  )}
                  {freeAdAvailable && (
                    <Text size="xsmall">
                      <Secondary>{t('free-lite-ad-text')}</Secondary>
                    </Text>
                  )}
                </Stack>
              </>
            )}
          </Fragment>
        )}
      </Stack>
    </Card>
  );
};
